import { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Heading,
  Paragraph,
  Skeleton,
} from '@digdir/designsystemet-react';
import type { HrWorkEnvironmentOverview } from '../../../../api/hrWorkEnvironment';
import { useUsesSafetyReps } from '../../../../hooks/queries/hms/useHmsSettingsQuery';
import { safetyRepText } from '../../safetyRepText';
import { useHrWorkEnvironmentOverviewQuery } from '../../../../hooks/queries/hr/useHrWorkEnvironmentOverviewQuery';
import { questionLanguageFor } from '../../../FormSubmission/workEnvironmentFormView';
import classes from './Overview.module.css';

export type SubpageKey =
  | 'measures'
  | 'safetyReps'
  | 'surveys'
  | 'requirements'
  | 'process'
  | 'risks'
  | 'teams';

/** A page opened from a card in the overview, with its heading and what it is for */
export const Subpage = ({
  page,
  values,
  children,
}: {
  page: SubpageKey;
  values?: Record<string, number>;
  children: ReactNode;
}): ReactElement => {
  const { t } = useTranslation();
  const text = safetyRepText(useUsesSafetyReps());
  return (
    <div className={classes.page} data-size="md">
      <header className={classes.hero}>
        <Heading level={1} data-size="lg">
          {t(`hrOverview.pages.${page}.title`)}
        </Heading>
        <Paragraph data-size="lg" className={classes.text}>
          {t(text(`hrOverview.pages.${page}.intro`), values)}
        </Paragraph>
      </header>
      {children}
    </div>
  );
};

/** The pages built on the overview share its data, so they open at once from the overview */
export const OverviewSubpage = ({
  page,
  values,
  children,
}: {
  page: SubpageKey;
  values?: (overview: HrWorkEnvironmentOverview) => Record<string, number>;
  children: (overview: HrWorkEnvironmentOverview) => ReactNode;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useHrWorkEnvironmentOverviewQuery(
    questionLanguageFor(i18n.language),
  );
  return (
    <Subpage page={page} values={data && values?.(data)}>
      {isLoading && <Skeleton variant="rectangle" width="100%" height={320} />}
      {isError && (
        <Alert data-color="danger">
          <Paragraph>{t('hrOverview.error')}</Paragraph>
        </Alert>
      )}
      {data && children(data)}
    </Subpage>
  );
};
